import { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Pack3D } from '../components/Pack3D'
import { PackConfigurator } from '../components/PackConfigurator'
import { useRip } from '../components/RipProvider'
import {
  RARITY_LABEL,
  RarityChip,
  Skeleton,
  Stat,
  TIER_ACCENT,
  TIER_LABEL,
  rarityClass,
} from '../components/ui'
import { usePack } from '../hooks/queries'
import type { OddsRow, PackStats } from '../lib/api'
import { compactNumber, dateOnly, percent, usd } from '../lib/format'
import { MAX_CARDS, MIN_CARDS, TIERS, configFromId, packId, summaryFor, type PackConfig } from '../lib/packs'

/**
 * One pack, configured.
 *
 * The URL *is* the configuration — `/pack/elite-7` — so a build is
 * shareable, the back button steps through it, and the odds query is keyed by
 * the exact thing it describes. Changing a control replaces the URL rather
 * than pushing, so the slider does not fill the history stack.
 */
export function PackPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const config = useMemo(() => configFromId(id), [id])

  const { data, isLoading } = usePack(packId(config))
  const { rip, pendingPackId } = useRip()

  // Resolved locally, so the price, the wrapper and the stats on the buy panel
  // track the slider immediately. The odds table below follows from the server.
  const pack = useMemo(() => summaryFor(config), [config])
  const accent = TIER_ACCENT[config.tier]

  const setConfig = useCallback(
    (next: PackConfig) => navigate(`/pack/${packId(next)}`, { replace: true }),
    [navigate],
  )

  return (
    <div className="py-6">
      {/* Tiers are the top-level choice, so they are tabs rather than another
          control buried in the panel: switching tier keeps your card count. */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {TIERS.map((tier) => {
          const on = tier === config.tier
          return (
            <button
              key={tier}
              onClick={() => setConfig({ ...config, tier })}
              aria-pressed={on}
              className="border px-4 py-2 text-sm font-semibold transition-colors"
              style={{
                borderColor: on ? TIER_ACCENT[tier] : 'var(--color-line)',
                background: on ? `${TIER_ACCENT[tier]}1a` : 'var(--color-surface)',
                color: on ? TIER_ACCENT[tier] : 'var(--color-muted)',
              }}
            >
              {TIER_LABEL[tier]}
            </button>
          )
        })}
      </div>

      <div className="panel relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(70% 100% at 30% 0%, ${accent}20 0%, transparent 62%)` }}
          aria-hidden
        />
        {/*
          Three columns only once there is room for three columns. The rail eats
          240px, so at `lg` a 1fr text column between a 240px pack and a 340px
          buy panel collapses to about 180px and the heading wraps one word per
          line. Below `xl` the buy panel takes a full-width row of its own
          instead, which is the thing that has to stay readable.
        */}
        <div className="relative grid gap-6 p-6 sm:p-8 lg:grid-cols-[200px_minmax(0,1fr)] lg:items-start xl:grid-cols-[220px_minmax(0,1fr)_minmax(330px,400px)]">
          <div className="mx-auto w-full max-w-[220px]">
            <Pack3D pack={pack} className="aspect-[3/4.1] w-full" idle />
          </div>

          <div className="min-w-0">
            <div
              className="text-[0.6875rem] font-bold tracking-[0.18em] uppercase"
              style={{ color: accent }}
            >
              Robinhood Chain
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">{pack.name}</h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed" style={{ color: 'var(--color-muted)' }}>
              {pack.tagline}
            </p>

            <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-4">
              <Stat label="From" value={usd(pack.basePrice, { whole: true })} />
              <Stat label="Cards" value={`${MIN_CARDS}–${MAX_CARDS}`} />
              <Stat label="Published RTP" value={percent(pack.rtp, 1)} sub="Exact, by calibration" />
            </dl>

            <p className="mt-5 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
              {data
                ? `${data.pool.size} tokens are drawable above a ${compactNumber(data.pool.minMarketCap)} market cap, priced ${dateOnly(data.pool.refreshedAt)}.`
                : 'Loading the drawable pool…'}
            </p>
          </div>

          <div className="lg:col-span-2 xl:col-span-1">
            <PackConfigurator
              config={config}
              onChange={setConfig}
              onRip={() => void rip(pack)}
              price={pack.price}
              rtp={pack.rtp}
              pending={pendingPackId === pack.id}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-start">
        {isLoading || !data ? (
          <Skeleton className="h-96" />
        ) : (
          <OddsTable odds={data.odds} rtp={pack.rtp} price={pack.price} />
        )}

        <div className="space-y-6">
          {data ? (
            <div className="panel p-5">
              <Distribution stats={data.stats} price={pack.price} />
            </div>
          ) : (
            <Skeleton className="h-72" />
          )}

          {data && <SlotBreakdown slots={data.pack.slots} />}

          <div className="panel p-5 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
            <div className="eyebrow mb-2">How this table is built</div>
            <p>
              Each rarity owns a fixed band of the value ladder, expressed as a multiple of the pack
              price. Values inside a band are drawn log-uniformly, so small outcomes stay common and
              large ones stay rare even within one rarity.
            </p>
            <p className="mt-2">
              Every band in this pack is then scaled by one shared factor so the expected value lands
              exactly on {percent(pack.rtp, 1)}. That factor is the entire house edge — there is no
              second adjustment applied anywhere else, and adding cards does not change it.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function OddsTable({ odds, rtp, price }: { odds: OddsRow[]; rtp: number; price: number }) {
  return (
    <div className="panel overflow-hidden">
      <div className="flex items-baseline justify-between border-b px-5 py-4">
        <h2 className="text-base font-semibold">Published odds</h2>
        <span className="tnum text-xs" style={{ color: 'var(--color-faint)' }}>
          {usd(price * rtp)} expected per {usd(price, { whole: true })}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left" style={{ color: 'var(--color-faint)' }}>
              <th className="px-5 py-2.5 text-[0.6875rem] font-semibold tracking-wider uppercase">
                Rarity
              </th>
              <th className="px-3 py-2.5 text-right text-[0.6875rem] font-semibold tracking-wider uppercase">
                Per pack
              </th>
              <th className="px-3 py-2.5 text-right text-[0.6875rem] font-semibold tracking-wider uppercase">
                Payout band
              </th>
              <th className="px-5 py-2.5 text-right text-[0.6875rem] font-semibold tracking-wider uppercase">
                Share of EV
              </th>
            </tr>
          </thead>
          <tbody>
            {odds.map((row) => (
              <tr key={row.rarity} className={`${rarityClass(row.rarity)} border-t`}>
                <td className="px-5 py-3">
                  <RarityChip rarity={row.rarity} />
                </td>
                <td className="tnum px-3 py-3 text-right font-medium">{percent(row.perPack)}</td>
                <td className="tnum px-3 py-3 text-right" style={{ color: 'var(--color-muted)' }}>
                  {usd(row.lo)} – {usd(row.hi)}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <div className="meter w-16">
                      <span style={{ width: `${row.evShare * 100}%` }} />
                    </div>
                    <span className="tnum w-11 text-right text-xs font-medium">
                      {percent(row.evShare, 1)}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Mean and median side by side. On a long-tailed payout these differ a lot,
 * and the median is the one that describes a typical pack — publishing only
 * the average is how these products get misread.
 */
function Distribution({ stats, price }: { stats: PackStats; price: number }) {
  const bars = [
    { label: 'Bottom 10%', value: stats.p10 },
    { label: 'Median', value: stats.median },
    { label: 'Average', value: stats.mean },
    { label: 'Top 10%', value: stats.p90 },
  ]
  const max = Math.max(...bars.map((b) => b.value), price)

  return (
    <div>
      <div className="eyebrow">What this pack actually returns</div>

      <div className="mt-3 space-y-3">
        {bars.map((bar) => (
          <div key={bar.label}>
            <div className="flex items-baseline justify-between text-xs">
              <span style={{ color: 'var(--color-muted)' }}>{bar.label}</span>
              <span className="tnum font-semibold">{usd(bar.value)}</span>
            </div>
            <div className="meter mt-1.5">
              <span
                style={{
                  width: `${(bar.value / max) * 100}%`,
                  background: bar.value >= price ? 'var(--color-up)' : 'var(--color-brand)',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <dl className="mt-5 flex flex-wrap gap-x-10 gap-y-3">
        <div>
          <dt className="eyebrow">Break even or better</dt>
          <dd className="tnum mt-1 text-lg font-bold">{percent(stats.breakEvenRate, 1)}</dd>
        </div>
        <div>
          <dt className="eyebrow">10× or better</dt>
          <dd className="tnum mt-1 text-lg font-bold">{percent(stats.moonRate, 2)}</dd>
        </div>
      </dl>

      <p className="mt-3 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
        From {stats.iterations.toLocaleString()} simulated opens against this exact configuration.
        The median sits below the average because a small number of very large pulls carry most of
        the expected value.
      </p>
    </div>
  )
}

function SlotBreakdown({
  slots,
}: {
  slots: Array<{ index: number; label: string; probabilities: Partial<Record<string, number>> }>
}) {
  return (
    <div className="panel overflow-hidden">
      <div className="border-b px-5 py-4">
        <h2 className="text-base font-semibold">Slot by slot</h2>
        <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
          Each card position rolls its own rarity independently.
        </p>
      </div>

      <div className="divide-y">
        {slots.map((slot) => (
          <div key={slot.index} className="px-5 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Card {slot.index + 1}
                {slot.label === 'Hit' && (
                  <span
                    className="ml-2 rounded-none px-1.5 py-0.5 text-[0.625rem] font-bold tracking-wider uppercase"
                    style={{ background: 'var(--color-brand-wash)', color: 'var(--color-brand)' }}
                  >
                    Hit
                  </span>
                )}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {Object.entries(slot.probabilities).map(([rarity, p]) => (
                <span key={rarity} className="tnum text-[0.6875rem]" style={{ color: 'var(--color-muted)' }}>
                  {RARITY_LABEL[rarity as keyof typeof RARITY_LABEL]}{' '}
                  <b style={{ color: 'var(--color-text)' }}>{percent(p ?? 0, 2)}</b>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
